import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Recycle, Info } from 'lucide-react';
import { getStyles } from './wizardConstants';
import FactorPickerInline from './FactorPickerInline';
import { toNumber } from '../../utils/numbers';
import { useLanguage } from '../../context/LanguageContext';

export const StepEndOfLife = ({ formData, treatments, addEndOfLifeEntry, updateEndOfLifeEntry, removeEndOfLifeEntry, isDark }) => {
  const s = getStyles(isDark);
  const { t } = useLanguage();

  return (
    <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('productWizard.endOfLife.title')}</h3>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {t('productWizard.endOfLife.subtitle')}
          </p>
        </div>
        <button onClick={addEndOfLifeEntry} data-testid="eol-add-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all flex-shrink-0">
          <Plus className="w-4 h-4" /> {t('common.add')}
        </button>
      </div>

      <div className={`p-3 rounded-xl flex items-start gap-2 ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
        <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          {t('productWizard.endOfLife.sectionHelp')}
        </p>
      </div>

      {formData.end_of_life.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border-2 border-dashed ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
          <Recycle className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {t('productWizard.endOfLife.emptyState')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.end_of_life.map((entry, index) => {
            const selectedFactor = treatments.find(t2 => t2.id === entry.emission_factor_id);
            const factorLabel = selectedFactor ? (selectedFactor.name_simple_fr || selectedFactor.source_product_name || selectedFactor.name) : t('productWizard.endOfLife.lineFallback').replace('{n}', index + 1);
            const q = toNumber(entry.quantity);
            const entryEmissions = selectedFactor && q > 0 ? q * (selectedFactor.value || 0) : 0;

            return (
              <div key={entry._key || index} className={s.section} data-testid={`eol-entry-${index}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Recycle className={`w-4 h-4 ${isDark ? 'text-sky-400' : 'text-sky-600'}`} />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{factorLabel}</span>
                    {entryEmissions > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700'}`}
                        data-testid={`eol-emissions-${index}`}>{entryEmissions.toFixed(3)} kgCO2e</span>
                    )}
                  </div>
                  <button onClick={() => removeEndOfLifeEntry(index)} data-testid={`eol-remove-${index}`}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <FactorPickerInline
                      factors={treatments}
                      categoryCode="fin_vie_produits"
                      selectedFactorId={entry.emission_factor_id}
                      onSelectFactor={(f) => {
                        updateEndOfLifeEntry(index, 'emission_factor_id', f.id);
                        updateEndOfLifeEntry(index, 'name', f.name_simple_fr || f.source_product_name || f.name || '');
                        updateEndOfLifeEntry(index, 'unit', f.default_unit || 'kg');
                      }}
                      label={t('productWizard.endOfLife.factorLabel')}
                    />
                  </div>
                  <div>
                    <label className={s.label}>{t('productWizard.endOfLife.quantityLabel').replace('{unit}', entry.unit || 'kg')}</label>
                    <input type="text" inputMode="decimal" value={entry.quantity === 0 ? '' : (entry.quantity ?? '')}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^[0-9]*[.,]?[0-9]*$/.test(v)) updateEndOfLifeEntry(index, 'quantity', v);
                      }}
                      className={s.input} placeholder="0" data-testid={`eol-quantity-${index}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};
